/**
 * Widget dashboard "Attività recente" — ultime N transizioni stato preventivo.
 * Aggrega gli eventi (creato, inviato, vinto, perso) dei preventivi,
 * li ordina cronologicamente e mostra i 10 più recenti con click navigation.
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileEdit, Send, Trophy, XCircle, Activity, ArrowRight } from 'lucide-react';

type EventType = 'created' | 'sent' | 'won' | 'lost';

interface ActivityEvent {
  estimateId: string;
  estimateName: string;
  totalAmount: number;
  type: EventType;
  at: Date;
}

interface DashboardActivityFeedProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  estimatesData: any[];
  /** Numero massimo eventi mostrati (default 10). */
  limit?: number;
}

const EVENT_META: Record<EventType, { label: string; icon: React.ReactNode; color: string }> = {
  created: { label: 'Creato', icon: <FileEdit className="h-3.5 w-3.5" />, color: 'text-slate-600' },
  sent: { label: 'Inviato', icon: <Send className="h-3.5 w-3.5" />, color: 'text-amber-600' },
  won: { label: 'Vinto', icon: <Trophy className="h-3.5 w-3.5" />, color: 'text-emerald-600' },
  lost: { label: 'Perso', icon: <XCircle className="h-3.5 w-3.5" />, color: 'text-red-600' },
};

function relativeTime(d: Date): string {
  const ms = Date.now() - d.getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'or ora';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h fa`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}gg fa`;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}

function euro(n: number): string {
  return `€ ${Math.round(n).toLocaleString('it-IT')}`;
}

const DashboardActivityFeed: React.FC<DashboardActivityFeedProps> = ({ estimatesData, limit = 10 }) => {
  const navigate = useNavigate();

  const events = useMemo<ActivityEvent[]>(() => {
    const out: ActivityEvent[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const e of (estimatesData ?? []) as any[]) {
      const base = {
        estimateId: e.id,
        estimateName: e.name || 'Preventivo',
        totalAmount: Number(e.total_amount ?? 0),
      };
      if (e.created_at) out.push({ ...base, type: 'created', at: new Date(e.created_at) });
      if (e.sent_at)    out.push({ ...base, type: 'sent',    at: new Date(e.sent_at) });
      if (e.won_at)     out.push({ ...base, type: 'won',     at: new Date(e.won_at) });
      if (e.lost_at)    out.push({ ...base, type: 'lost',    at: new Date(e.lost_at) });
    }
    return out.sort((a, b) => b.at.getTime() - a.at.getTime()).slice(0, limit);
  }, [estimatesData, limit]);

  if (events.length === 0) {
    return null;
  }

  return (
    <Card className="animate-fade-in">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          Attività recente
        </CardTitle>
        <CardDescription>
          Le ultime transizioni di stato dei preventivi della tua organizzazione
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {events.map((ev, idx) => {
            const meta = EVENT_META[ev.type];
            return (
              <button
                key={`${ev.estimateId}-${ev.type}-${idx}`}
                onClick={() => navigate(`/estimates/${ev.estimateId}/manage`)}
                className="w-full text-left flex items-center gap-3 py-2 px-2 rounded hover:bg-muted/40 transition-colors group"
              >
                <div className={`p-1.5 rounded-full bg-muted/60 ${meta.color}`}>
                  {meta.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold uppercase tracking-wide ${meta.color}`}>
                      {meta.label}
                    </span>
                    <span className="text-sm truncate">{ev.estimateName}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{relativeTime(ev.at)}</div>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  <span className="text-sm font-mono tabular-nums">{euro(ev.totalAmount)}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardActivityFeed;
